import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { Link as RouterLink } from "react-router-dom";

import { chargebeePromise } from "../../../utils/chargebee";
import {CancelablePromise, SubscriptionService} from "../../../services/restserver-openapi";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { getUserEntitlementsAsync, getUserSubscriptionsAsync } from "../../../store/navBar/userSlice";

export type EarlyAccessModalProps = {
    enabled?: boolean;

    entitlementFeatureId?: string; // default: 'vanliferhasearlyaccess'
    entitlementValue?: string; // default: 'true'
    itemPriceId?: string; // default: 'vanlifer-early-access-CHF-Yearly'

    autoOpen?: boolean;

    oncePerSession?: boolean;
    storageKey?: string;

    loginPath?: string;

    onPurchased?: () => void;
};

const DEFAULTS = {
    enabled: true,
    entitlementFeatureId: "vanliferhasearlyaccess",
    entitlementValue: "true",
    itemPriceId: "vanlifer-early-access-CHF-Yearly",
    autoOpen: true,
    oncePerSession: true,
    storageKey: "vlt_early_access_modal_shown",
    loginPath: "/login",
};

const EarlyAccessModal: React.FC<EarlyAccessModalProps> = (props) => {
    const cfg = { ...DEFAULTS, ...props };

    const theme = useTheme();
    const fullScreen = !useMediaQuery(theme.breakpoints.up("sm"));
    const dispatch = useAppDispatch();

    const entitlements = useAppSelector((s) => s.user.entitlements ?? []);
    const userSpecificationId = useAppSelector((s) => s.user.userSpecificationId);
    const isAuthenticated = Boolean(userSpecificationId);

    // Guard gegen StrictMode double-effects
    const didInitRef = useRef(false);
    const didAutoOpenRef = useRef(false);

    const [open, setOpen] = useState(false);
    const [decisionReady, setDecisionReady] = useState(false);


    const hasEarlyAccess = useMemo(() => {
        return entitlements.some(
            (e) =>
                e.featureId === cfg.entitlementFeatureId &&
                String(e.value) === cfg.entitlementValue
        );
    }, [entitlements, cfg.entitlementFeatureId, cfg.entitlementValue]);

    const alreadyShownThisSession = useMemo(() => {
        if (!cfg.oncePerSession) return false;
        try {
            return sessionStorage.getItem(cfg.storageKey) === "1";
        } catch {
            return false;
        }
    }, [cfg.oncePerSession, cfg.storageKey]);

    const markShown = useCallback(() => {
        if (!cfg.oncePerSession) return;
        try {
            sessionStorage.setItem(cfg.storageKey, "1");
        } catch {
            // ignore
        }
    }, [cfg.oncePerSession, cfg.storageKey]);

    useEffect(() => {
        if (hasEarlyAccess && open) setOpen(false);
    }, [hasEarlyAccess, open]);


    useEffect(() => {
        if (!cfg.enabled || !cfg.autoOpen) return;

        // pro "enabled"-Phase nur 1x entscheiden
        if (didAutoOpenRef.current) return;

        // wenn user das modal in dieser session schon geschlossen hat: nichts mehr machen
        if (alreadyShownThisSession) {
            didAutoOpenRef.current = true;
            setOpen(false);
            setDecisionReady(true);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                // Optional aber empfehlenswert: nicht für Gäste öffnen (verhindert Auth-Transition Flash)
                // Wenn du Gäste wirklich triggern willst, sag Bescheid.
                if (!isAuthenticated) {
                    didAutoOpenRef.current = true;
                    setOpen(false);
                    setDecisionReady(true);
                    return;
                }

                // Immer “hart” vom Server entscheiden, nicht anhand initial leerem Store
                const action: any = await dispatch(getUserEntitlementsAsync() as any);
                const fetched = action?.payload ?? [];

                const has = Array.isArray(fetched) && fetched.some(
                    (e: any) =>
                        e.featureId === cfg.entitlementFeatureId &&
                        String(e.value) === cfg.entitlementValue
                );

                if (cancelled) return;

                didAutoOpenRef.current = true;
                setOpen(!has);              // ✅ nur öffnen wenn Entitlement NICHT vorhanden
                setDecisionReady(true);     // ✅ erst jetzt rendern
            } catch {
                // Fail-safe: bei Fehler lieber nicht nerven
                if (cancelled) return;
                didAutoOpenRef.current = true;
                setOpen(false);
                setDecisionReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        cfg.enabled,
        cfg.autoOpen,
        cfg.entitlementFeatureId,
        cfg.entitlementValue,
        alreadyShownThisSession,
        isAuthenticated,
        dispatch,
    ]);


    const handleClose = useCallback(() => {
        setOpen(false);
        markShown();
    }, [markShown]);

    async function openSelfServicePortal() {
        const cbInstance = await chargebeePromise;

        if (!cbInstance) {
            console.error('Chargebee not loaded');
            return;
        }

        const hostedPagePromise : CancelablePromise<any> = SubscriptionService.requestSubscriptionIntent('vanlifer-early-access-CHF-Yearly', true);

        cbInstance.openCheckout({
            hostedPage: function() {
                return hostedPagePromise;
            },
            success: function() {
                dispatch(getUserSubscriptionsAsync())
            }, close(): void {
            }, layout: "in_app", step(currentStep: string): void {
            }
        });
    }

    if (!cfg.enabled || !decisionReady) return null;


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                    background: "linear-gradient(135deg, #1f3a34 0%, #1B2934 100%)",
                    color: "#CCDA10",
                },
            }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h5" fontWeight={800}>
                    Sei von Anfang an dabei!
                </Typography>

                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 12,
                        top: 12,
                        color: "rgba(255,255,255,0.9)",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                    Mit <b style={{ color: "white" }}>Early Access</b> bekommst du exklusive Einblicke,
                    die neuesten Features zuerst und du kannst direkt Feedback geben – zum Preis von
                    einem Kaffee pro Monat.
                </Typography>

                <Box mt={2}>
                    <List>
                        {[
                            "Exklusive Updates und Features vor allen anderen.",
                            "Direktes Feedback geben und die Lösung mitgestalten.",
                            "Teil der Community werden und Ideen einbringen.",
                        ].map((text) => (
                            <ListItem key={text} disableGutters>
                                <ListItemIcon sx={{ minWidth: 34 }}>
                                    <CheckIcon color="secondary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={text}
                                    primaryTypographyProps={{
                                        sx: { color: "rgba(255,255,255,0.85)" },
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />

                <Box display="flex" gap={1.5} flexWrap="wrap" justifyContent="center">
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={openSelfServicePortal}
                    >
                        Early Access Mitglied werden
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ borderColor: "rgba(255,255,255,0.35)", color: "white" }}
                        onClick={handleClose}
                    >
                        Später
                    </Button>
                </Box>

                <Typography
                    variant="caption"
                    display="block"
                    textAlign="center"
                    sx={{ mt: 2, color: "rgba(255,255,255,0.65)" }}
                >
                    Tipp: Wenn du „Später“ klickst, erscheint es in dieser Session nicht nochmals.
                </Typography>
            </DialogContent>
        </Dialog>
    );
};

export default EarlyAccessModal;
